import React, { useRef } from 'react';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { convertToVwMob } from '../../../Utils/responsive';
import RichTextEditor, { RichTextEditorHandle } from '../../../components/common/RichTextEditor';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import { useTranslation } from 'react-i18next';
import loadingLottie from '../../../assets/Icon/lottie/Insider-loading.lottie';
import DSModal from '../../../components/designSystem/DSModal';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');
const FOCUSED_HEADER_HEIGHT = 56;
const KEYBOARD_TOOLBAR_SCROLL_PAD = 250;
const EDITOR_BOTTOM_SPACER_PX = 200;

interface CreatePostModalMobProps {
  isOpen: boolean;
  isEditMode?: boolean;
  currentStep: number;
  mediaType: 'image' | 'video' | 'none';
  previewUrl: string | null;
  videoUrl: string;
  postContent: string;
  previewHtml?: string;
  isLoading?: boolean;
  onClose: () => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNextStep: () => void;
  onPrevStep: () => void;
  onMediaTypeChange: (type: 'image' | 'video' | 'none') => void;
  onContentChange: (content: string) => void;
  onVideoUrlChange: (value: string) => void;
  onRemoveMainMedia: () => void;
}

const CreatePostModalMob: React.FC<CreatePostModalMobProps> = ({
  isOpen,
  isEditMode = false,
  currentStep,
  mediaType,
  previewUrl,
  videoUrl,
  postContent,
  previewHtml,
  isLoading = false,
  onClose,
  onFileSelect,
  onNextStep,
  onPrevStep,
  onMediaTypeChange,
  onContentChange,
  onVideoUrlChange,
  onRemoveMainMedia,
}) => {
  const { t } = useTranslation('translation');
  const processingLabel = stripEllipsis(t('common.actions.processing'));
  const [isTextareaFocused, setIsTextareaFocused] = React.useState(false);
  const [videoUrlFocused, setVideoUrlFocused] = React.useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = React.useState(false);

  const editorRef = useRef<RichTextEditorHandle>(null);
  const prevStepRef = useRef<number | null>(null);

  const keyboardFixedFooterRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);

  const liftEditorScroll = React.useCallback((amount: number) => {
    if (currentStep !== 2) return;
    window.requestAnimationFrame(() => {
      scrollableContentRef.current?.scrollBy({ top: amount, behavior: 'auto' });
    });
  }, [currentStep]);

  const scrollStep2EditorIntoView = React.useCallback(() => {
    const scrollEl = scrollableContentRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;

    const pad = 48;
    const scrollRect = scrollEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const sel = window.getSelection();
    let focusTop = editorRect.top;
    let focusBottom = editorRect.bottom;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const r = range.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        focusTop = r.top;
        focusBottom = r.bottom;
      } else {
        focusTop = editorRect.top;
        focusBottom = editorRect.bottom;
      }
    }

    if (focusBottom > scrollRect.bottom - pad) {
      scrollEl.scrollTop += focusBottom - (scrollRect.bottom - pad);
    } else if (focusTop < scrollRect.top + pad) {
      scrollEl.scrollTop += focusTop - (scrollRect.top + pad);
    }
  }, []);

  const scrollStep2TailIntoView = React.useCallback(() => {
    const scrollEl = scrollableContentRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;
    const images = editorEl.querySelectorAll<HTMLElement>('figure[data-embed="story-image"]');
    const lastImage = images.length ? images[images.length - 1] : null;
    const anchorRect = lastImage?.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const visibleBottom = scrollRect.bottom - 156;
    const targetBottom = Math.max(anchorRect ? anchorRect.bottom + 36 : 0, editorRect.bottom);
    if (targetBottom > visibleBottom) {
      scrollEl.scrollTop += targetBottom - visibleBottom;
    }
  }, []);

  const focusStep2Editor = React.useCallback(() => {
    setIsTextareaFocused(true);
    const timeoutIds: number[] = [];
    const rafIds: number[] = [];
    const run = () => {
      editorRef.current?.focusAtEnd();
      const rid = requestAnimationFrame(() => {
        if (scrollableContentRef.current) {
          scrollableContentRef.current.scrollTop = scrollableContentRef.current.scrollHeight;
        }
        scrollStep2EditorIntoView();
        scrollStep2TailIntoView();
        liftEditorScroll(220);
        editorRef.current?.focus();
      });
      rafIds.push(rid);
    };

    [0, 80, 180, 320].forEach((delay) => {
      const tid = window.setTimeout(() => {
        const rid = requestAnimationFrame(run);
        rafIds.push(rid);
      }, delay);
      timeoutIds.push(tid);
    });

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [liftEditorScroll, scrollStep2EditorIntoView, scrollStep2TailIntoView]);

  React.useEffect(() => {
    if (!isTextareaFocused || !window.visualViewport) return;

    const handleVisualViewportChange = () => {
      if (!keyboardFixedFooterRef.current || !window.visualViewport) return;
      
      const visualViewport = window.visualViewport;
      const layoutHeight = document.documentElement.clientHeight;
      const visualHeight = visualViewport.height;
      const visualTop = visualViewport.offsetTop;

      const offset = Math.max(0, layoutHeight - visualHeight - visualTop);
      
      keyboardFixedFooterRef.current.style.bottom = `${offset}px`;
    };

    window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
    
    handleVisualViewportChange();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleVisualViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleVisualViewportChange);
    };
  }, [isTextareaFocused]);

  React.useEffect(() => {
    if (!isOpen) return;
    window.requestAnimationFrame(() => {
      scrollableContentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, [currentStep, isOpen]);

  const videoUrlTrimmed = videoUrl.trim();
  const videoUrlValid = React.useMemo(() => {
    if (!videoUrlTrimmed) return false;
    try {
      const url = new URL(videoUrlTrimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }, [videoUrlTrimmed]);
  const showVideoUrlError = Boolean(videoUrlTrimmed) && !videoUrlValid;
  const videoEmbedUrl = React.useMemo(() => {
    if (!videoUrlValid) return '';
    try {
      const url = new URL(videoUrlTrimmed);
      const host = url.hostname.replace('www.', '');
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (host.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  }, [videoUrlTrimmed, videoUrlValid]);
  const videoPreviewSrc = React.useMemo(() => {
    if (!videoUrlValid) return '';
    return videoEmbedUrl || videoUrlTrimmed;
  }, [videoEmbedUrl, videoUrlTrimmed, videoUrlValid]);

  const embeddedImageCount = React.useMemo(() => {
    const doc = new DOMParser().parseFromString(postContent, 'text/html');
    return doc.querySelectorAll('figure[data-embed="story-image"]').length;
  }, [postContent]);

  const prevEmbeddedImageCountRef = useRef(embeddedImageCount);

  React.useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      htmlOverflow: html.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.position = prev.bodyPosition;
      document.body.style.top = prev.bodyTop;
      document.body.style.left = prev.bodyLeft;
      document.body.style.right = prev.bodyRight;
      document.body.style.width = prev.bodyWidth;
      document.body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) return;
    setExitConfirmOpen(false);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      prevStepRef.current = null;
      return;
    }
    if (currentStep === 2 && prevStepRef.current !== 2) {
      prevStepRef.current = 2;
      return focusStep2Editor();
    }
    prevStepRef.current = currentStep;
    return undefined;
  }, [isOpen, currentStep, focusStep2Editor]);

  React.useLayoutEffect(() => {
    if (!isOpen || currentStep !== 2) return;
    setIsTextareaFocused(true);
    editorRef.current?.focusAtEnd();
  }, [isOpen, currentStep]);

  React.useEffect(() => {
    if (!isOpen || currentStep !== 2 || !isTextareaFocused) return;
    const t = window.setTimeout(() => {
      editorRef.current?.focus();
      scrollStep2EditorIntoView();
      scrollStep2TailIntoView();
    }, 40);
    return () => window.clearTimeout(t);
  }, [isOpen, currentStep, isTextareaFocused, scrollStep2EditorIntoView, scrollStep2TailIntoView]);

  React.useEffect(() => {
    if (!isOpen || currentStep !== 2 || !isEditMode) return;
    const timeoutIds: number[] = [];
    const rafIds: number[] = [];
    const run = () => {
      setIsTextareaFocused(true);
      if (scrollableContentRef.current) {
        scrollableContentRef.current.scrollTop = scrollableContentRef.current.scrollHeight;
      }
      editorRef.current?.focusAtEnd();
      const rid = requestAnimationFrame(() => {
        scrollStep2TailIntoView();
        editorRef.current?.focus();
      });
      rafIds.push(rid);
    };
    [0, 120, 260, 420].forEach((delay) => {
      const tid = window.setTimeout(() => {
        const rid = requestAnimationFrame(run);
        rafIds.push(rid);
      }, delay);
      timeoutIds.push(tid);
    });
    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [isOpen, currentStep, isEditMode, postContent, scrollStep2TailIntoView]);

  React.useEffect(() => {
    if (currentStep !== 2) {
      setIsTextareaFocused(false);
      prevEmbeddedImageCountRef.current = embeddedImageCount;
      return;
    }
    if (!isOpen) return;
    if (embeddedImageCount > prevEmbeddedImageCountRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTextareaFocused(true);
          editorRef.current?.focus();
          scrollStep2EditorIntoView();
          scrollStep2TailIntoView();
          liftEditorScroll(220);
        });
      });
    }
    prevEmbeddedImageCountRef.current = embeddedImageCount;
  }, [currentStep, embeddedImageCount, isOpen, scrollStep2EditorIntoView, scrollStep2TailIntoView, liftEditorScroll]);

  const isStep1Complete = mediaType === 'none'
    || (mediaType === 'image' ? previewUrl !== null : videoUrlValid);
  const isNextDisabled = currentStep === 1 && !isStep1Complete;

  const hasPreview = mediaType === 'image'
    ? Boolean(previewUrl)
    : mediaType === 'video'
      ? Boolean(videoPreviewSrc)
      : false;
  const hasPreviewSummary = mediaType === 'image'
    ? Boolean(previewUrl)
    : mediaType === 'video'
      ? Boolean(videoPreviewSrc)
      : false;

  if (!isOpen) return null;

  const handleRequestClose = () => {
    (document.activeElement as HTMLElement | null)?.blur?.();
    setIsTextareaFocused(false);
    onClose();
  };

  const handleConfirmExit = () => {
    setExitConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <ModalOverlay>
        <ModalContent>
          {currentStep === 2 ? (
          <EditHeader>
            <EditHeaderButton onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              (document.activeElement as HTMLElement | null)?.blur?.();
              setIsTextareaFocused(false);
              window.requestAnimationFrame(() => {
                onPrevStep();
              });
            }}>{t('common.actions.close')}</EditHeaderButton>
            <EditHeaderButton primary onClick={() => {
              (document.activeElement as HTMLElement | null)?.blur?.();
              setIsTextareaFocused(false);
              onNextStep();
            }}>{t('common.actions.save')}</EditHeaderButton>
          </EditHeader>
        ) : (
          <>
            <ModalHeader>
              <ModalTitle>{isEditMode ? t('page.postManagement.modal.title.edit') : t('page.postManagement.modal.title.create')}</ModalTitle>
              <CloseButton onClick={handleRequestClose}>✕</CloseButton>
            </ModalHeader>

          </>
        )}

        <ScrollableContent
          ref={scrollableContentRef}
          $topPad={currentStep === 2 ? FOCUSED_HEADER_HEIGHT : 0}
          $bottomPad={isTextareaFocused ? KEYBOARD_TOOLBAR_SCROLL_PAD : 0}
        >
          {currentStep !== 2 && (
            <StepIndicatorWrapper>
              <StepIndicator>
                <Step active={currentStep === 1}>
                  <StepNumber active={currentStep === 1}>1</StepNumber>
                </Step>
                <StepLine />
                <Step active={currentStep === 2}>
                  <StepNumber active={currentStep === 2}>2</StepNumber>
                </Step>
                <StepLine />
                <Step active={currentStep === 3}>
                  <StepNumber active={currentStep === 3}>3</StepNumber>
                </Step>
              </StepIndicator>
              <StepLabelCenter>
                {currentStep === 1 && t('page.postManagement.modal.steps.media')}
                {currentStep === 2 && t('page.postManagement.modal.steps.story')}
                {currentStep === 3 && t('page.postManagement.modal.steps.preview')}
              </StepLabelCenter>
            </StepIndicatorWrapper>
          )}
          {currentStep === 1 && (
            <ModalBody>
              <SectionTitle>{t('page.postManagement.modal.mediaSectionTitle')}</SectionTitle>
              
              <MediaTypeSelector>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="mediaType"
                    value="image"
                    checked={mediaType === 'image'}
                    onChange={() => onMediaTypeChange('image')}
                  />
                  <RadioLabel>{t('page.postManagement.modal.mediaType.image')}</RadioLabel>
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="mediaType"
                    value="video"
                    checked={mediaType === 'video'}
                    onChange={() => onMediaTypeChange('video')}
                  />
                  <RadioLabel>{t('page.postManagement.modal.mediaType.video')}</RadioLabel>
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="mediaType"
                    value="none"
                    checked={mediaType === 'none'}
                    onChange={() => onMediaTypeChange('none')}
                  />
                  <RadioLabel>{t('page.postManagement.modal.mediaType.none')}</RadioLabel>
                </RadioOption>
              </MediaTypeSelector>

              {mediaType === 'video' ? (
                <>
                  <VideoUrlRow>
                    <VideoUrlField
                      $state={showVideoUrlError ? 'error' : videoUrlFocused ? 'focus' : videoUrlTrimmed ? 'complete' : 'default'}
                    >
                      <VideoUrlText>
                        {(videoUrlFocused || videoUrlTrimmed) && (
                          <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>
                        )}
                        <VideoUrlInput
                          placeholder={videoUrlFocused || videoUrlTrimmed ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                          value={videoUrl}
                          onChange={(e) => {
                            onVideoUrlChange(e.target.value);
                          }}
                          onFocus={() => setVideoUrlFocused(true)}
                          onBlur={() => setVideoUrlFocused(false)}
                        />
                      </VideoUrlText>
                      {videoUrlTrimmed ? (
                        <VideoUrlClear
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            onVideoUrlChange('');
                          }}
                          aria-label="clear"
                        >
                          ×
                        </VideoUrlClear>
                      ) : null}
                    </VideoUrlField>
                  </VideoUrlRow>
                  {showVideoUrlError ? (
                    <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError>
                  ) : null}
                </>
              ) : mediaType === 'image' ? (
                <FileUploadButton>
                  <input
                    id="file-upload-mob"
                    type="file"
                    accept="image/*"
                    onChange={onFileSelect}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="file-upload-mob">{t('page.postManagement.modal.uploadButton')}</label>
                </FileUploadButton>
              ) : null}

              <PreviewArea hasImage={hasPreview}>
                {mediaType === 'none' ? (
                  <PreviewPlaceholder>
                    <PlaceholderText>{t('page.projectSettings.media.empty')}</PlaceholderText>
                    <PlaceholderIcon>
                      <PreviewNoneIcon
                        xmlns="http://www.w3.org/2000/svg"
                        width="41"
                        height="41"
                        viewBox="0 0 41 41"
                        fill="none"
                        aria-hidden
                        focusable="false"
                      >
                        <path d="M14 16.0833H14.0208M26.5 16.0833H26.5208M14 26.5H26.5M1.5 20.25C1.5 22.7123 1.98498 25.1505 2.92726 27.4253C3.86953 29.7002 5.25065 31.7672 6.99175 33.5082C8.73285 35.2493 10.7998 36.6305 13.0747 37.5727C15.3495 38.515 17.7877 39 20.25 39C22.7123 39 25.1505 38.515 27.4253 37.5727C29.7002 36.6305 31.7672 35.2493 33.5082 33.5082C35.2493 31.7672 36.6305 29.7002 37.5727 27.4253C38.515 25.1505 39 22.7123 39 20.25C39 17.7877 38.515 15.3495 37.5727 13.0747C36.6305 10.7998 35.2493 8.73285 33.5082 6.99175C31.7672 5.25065 29.7002 3.86953 27.4253 2.92726C25.1505 1.98498 22.7123 1.5 20.25 1.5C17.7877 1.5 15.3495 1.98498 13.0747 2.92726C10.7998 3.86953 8.73285 5.25065 6.99175 6.99175C5.25065 8.73285 3.86953 10.7998 2.92726 13.0747C1.98498 15.3495 1.5 17.7877 1.5 20.25Z" stroke="#BCBCBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </PreviewNoneIcon>
                    </PlaceholderIcon>
                  </PreviewPlaceholder>
                ) : mediaType === 'video' ? (
                  videoPreviewSrc ? (
                    <PreviewIframe
                      src={videoPreviewSrc}
                      title="video preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <PreviewPlaceholder>
                      <PlaceholderText>
                        {t('page.projectSettings.media.linkPreviewHintMobile', {
                          defaultValue: t('page.projectSettings.media.linkPreviewHint'),
                        })}
                      </PlaceholderText>
                      <PlaceholderIcon>
                        <PreviewVideoIcon
                          xmlns="http://www.w3.org/2000/svg"
                          width="70"
                          height="70"
                          viewBox="0 0 70 70"
                          fill="none"
                          aria-hidden
                          focusable="false"
                        >
                          <path d="M29.1665 24.7915L46.6665 34.9998L29.1665 45.2082V24.7915Z" fill="#BCBCBC" />
                          <path d="M35 14.5835C61.25 14.5835 61.25 14.5835 61.25 35.0002C61.25 55.4168 61.25 55.4168 35 55.4168C8.75 55.4168 8.75 55.4168 8.75 35.0002C8.75 14.5835 8.75 14.5835 35 14.5835Z" stroke="#BCBCBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </PreviewVideoIcon>
                      </PlaceholderIcon>
                    </PreviewPlaceholder>
                  )
                ) : previewUrl ? (
                  <>
                    <PreviewImage src={previewUrl} alt={t('page.postManagement.modal.previewAlt')} />
                    <RemoveMediaButton onClick={onRemoveMainMedia}>✕</RemoveMediaButton>
                  </>
                ) : (
                  <PreviewPlaceholder>
                    <PlaceholderText>
                      {t('page.postManagement.modal.previewHintImageMobile', {
                        defaultValue: t('page.postManagement.modal.previewHintImage'),
                      })}
                    </PlaceholderText>
                    <PlaceholderIcon>
                      <img src="/Image/posts/intro_image.png" alt="placeholder" width="60" height="60" />
                    </PlaceholderIcon>
                  </PreviewPlaceholder>
                )}
              </PreviewArea>
            </ModalBody>
          )}

          {currentStep === 2 && (
            <Step2Container>
              <EditorTapArea
                $focused={isTextareaFocused}
                onPointerDown={() => {
                  window.requestAnimationFrame(() => {
                    editorRef.current?.focusAtEnd();
                    scrollStep2TailIntoView();
                  });
                }}
              >
                <RichTextEditor
                  ref={editorRef}
                  value={postContent}
                  onChange={onContentChange}
                  onEnterKey={() => liftEditorScroll(96)}
                  onImageInserted={() => {
                    requestAnimationFrame(() => {
                      setIsTextareaFocused(true);
                      editorRef.current?.focus();
                      window.setTimeout(() => {
                        editorRef.current?.focus();
                      }, 60);
                      requestAnimationFrame(() => {
                        scrollStep2EditorIntoView();
                        scrollStep2TailIntoView();
                        liftEditorScroll(220);
                      });
                    });
                  }}
                  editorBottomPaddingPx={isTextareaFocused ? EDITOR_BOTTOM_SPACER_PX : 0}
                  onFocus={() => {
                    setIsTextareaFocused(true);
                    window.requestAnimationFrame(() => {
                      scrollableContentRef.current?.scrollBy({ top: 180, behavior: 'auto' });
                      scrollStep2EditorIntoView();
                      scrollStep2TailIntoView();
                    });
                  }}
                  onBlur={() => {
                    if (!isOpen || currentStep !== 2) return;
                    window.requestAnimationFrame(() => {
                      setIsTextareaFocused(true);
                      editorRef.current?.focusAtEnd();
                      scrollStep2TailIntoView();
                    });
                  }}
                  placeholder={t('page.postManagement.modal.storyPlaceholder')}
                  pictureIconSrc="/Image/posts/photoIcon.png"
                  maxImages={3}
                  imageCount={embeddedImageCount}
                  showToolbar={false}
                  pinnedImageSrc={isTextareaFocused && mediaType === 'image' && previewUrl ? previewUrl : undefined}
                  pinnedVideoEmbedUrl={isTextareaFocused && mediaType === 'video' && videoPreviewSrc ? videoPreviewSrc : undefined}
                  pinnedInsideEditor
                />
              </EditorTapArea>
            </Step2Container>
          )}

          {currentStep === 3 && (
            <Step3Container>
              <Step3Desc>{t('page.postManagement.modal.previewSectionTitle')}</Step3Desc>
              
              {hasPreviewSummary && (
                <Step2PreviewArea>
                  {mediaType === 'image' && previewUrl ? (
                    <PreviewImage src={previewUrl} alt={t('page.postManagement.modal.previewAlt')} />
                  ) : mediaType === 'video' && videoPreviewSrc ? (
                    <PreviewIframe
                      src={videoPreviewSrc}
                      title="video preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : null}
                  {mediaType === 'image' && previewUrl && (
                    <MainImageBadge>{t('page.postManagement.modal.mainBadge')}</MainImageBadge>
                  )}
                </Step2PreviewArea>
              )}
              
              <PreviewContent $hasMedia={hasPreviewSummary}>
                <SafeHtmlViewer html={previewHtml ?? postContent} />
              </PreviewContent>
            </Step3Container>
          )}
        </ScrollableContent>

        {currentStep === 2 ? (
          <KeyboardFixedFooter className="keyboard-toolbar" ref={keyboardFixedFooterRef}>
            <AddPhotoButton
              type="button"
              onClick={() => editorRef.current?.openImagePicker()}
              disabled={embeddedImageCount >= 3}
              onMouseDown={(e) => e.preventDefault()}
            >
              <AddPhotoLabel>
                <AddPhotoIcon src="/Image/posts/black_photo.png" alt={t('page.postManagement.modal.photoAlt')} />
                {t('page.postManagement.modal.addPhoto', { count: embeddedImageCount })}
              </AddPhotoLabel>
            </AddPhotoButton>
          </KeyboardFixedFooter>
        ) : (
          <ModalFooter>
            {currentStep === 1 && <FooterSpacer aria-hidden="true" />}
            {currentStep > 1 && (
              <BackButton onClick={onPrevStep} disabled={isLoading}>{t('common.actions.back')}</BackButton>
            )}
            <NextButton
              onClick={onNextStep}
              disabled={isNextDisabled || isLoading}
              $loading={isLoading}
              aria-busy={isLoading}
              aria-label={isLoading ? processingLabel : undefined}
            >
              {isLoading ? (
                <LoadingIcon
                  src={loadingLottie}
                  autoplay
                  loop
                  aria-hidden
                />
              ) : (
                currentStep === 3 ? t('common.actions.complete') : t('common.actions.next')
              )}
            </NextButton>
          </ModalFooter>
        )}
        </ModalContent>
      </ModalOverlay>

      <DSModal
        open={exitConfirmOpen}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmOpen(false)}
        actions={[
          { label: t('common.exitConfirm.leave'), variant: 'secondary', onClick: handleConfirmExit },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmOpen(false) },
        ]}
      />
    </>
  );
};

// 모달 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 1000;
  overflow: hidden;
  overscroll-behavior: none;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  ${({ theme }) => theme.typography.head.semiBold24}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${convertToVwMob(28)};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  cursor: pointer;
  padding: 0;
  display: flex;
`;

const EditHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1003;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const EditHeaderButton = styled.button<{ primary?: boolean }>`
  background: none;
  border: none;
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: ${convertToVwMob(20)};
  color: ${({ primary, theme }) => primary ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
  cursor: pointer;
  padding: 0;
`;

const StepIndicatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${convertToVwMob(24)} 0;
  flex-shrink: 0;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
`;

const Step = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepNumber = styled.div<{ active: boolean }>`
  width: ${({ active }) => active ? convertToVwMob(30) : convertToVwMob(20)};
  height: ${({ active }) => active ? convertToVwMob(30) : convertToVwMob(20)};
  border-radius: 50%;
  background: ${({ active }) => active ? '#2BC2E1' : '#DFDFDF'};
  color: ${({ theme }) => theme.colors.grayScale[0]};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.contents.regular20}
  font-size: ${({ active }) => active ? convertToVwMob(16) : convertToVwMob(14)};
`;

const StepLabelCenter = styled.div`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  text-align: center;
  margin-top: ${convertToVwMob(10)};
`;

const StepLine = styled.div`
  width: ${convertToVwMob(44)};
  height: 2px;
  background: #e7e7e7;
`;

const ScrollableContent = styled.div<{ $topPad?: number; $bottomPad?: number }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: ${({ $topPad }) => ($topPad ? convertToVwMob($topPad) : 0)};
  padding-bottom: ${({ $bottomPad }) => ($bottomPad ? convertToVwMob($bottomPad) : 0)};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ModalBody = styled.div`
  padding: ${convertToVwMob(2)} ${convertToVwMob(20)};
  padding-bottom: ${convertToVwMob(18)};
`;

const SectionTitle = styled.h3`
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0 0 ${convertToVwMob(20)} 0;
`;

const MediaTypeSelector = styled.div`
  display: flex;
  gap: ${convertToVwMob(24)};
  margin-bottom: ${convertToVwMob(12)};
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  cursor: pointer;
`;

const RadioInput = styled.input`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  min-width: ${convertToVwMob(20)};
  min-height: ${convertToVwMob(20)};
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  border-radius: 50%;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  position: relative;
  flex-shrink: 0;
  margin: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2bc2e1;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.15s ease;
  }

  &:checked::after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const RadioLabel = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const FileUploadButton = styled.div<{ disabled?: boolean }>`
  margin-bottom: ${convertToVwMob(20)};
  
  label {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: ${convertToVwMob(56)};
    background: #EBFCFF;
    border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40]};
    border-radius: ${convertToVwMob(12)};
    ${({ theme }) => theme.typography.button.medium20}
    color: ${({ theme }) => theme.colors.primaryBlue[40]};
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  }
`;

const VideoUrlRow = styled.div`
  margin-bottom: ${convertToVwMob(20)};
  display: flex;
  align-items: stretch;
  gap: ${convertToVwMob(10)};
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: ${convertToVwMob(6)};
  border: ${convertToVwMob(2)} solid
    ${({ $state }) =>
      $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: ${convertToVwMob(10)} ${convertToVwMob(12)};
  min-height: ${convertToVwMob(56)};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 700;
  color: #17171b;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
  }
`;

const VideoUrlClear = styled.button`
  width: ${convertToVwMob(28)};
  height: ${convertToVwMob(28)};
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: ${convertToVwMob(18)};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-top: ${convertToVwMob(6)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  height: ${convertToVwMob(56)};
  width: ${convertToVwMob(96)};
  border-radius: ${convertToVwMob(12)};
  border: 1px solid #dfdfdf;
  background: #ffffff;
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const PreviewArea = styled.div<{ hasImage: boolean }>`
  width: 100%;
  height: ${convertToVwMob(220)};
  background: #F0F0F0;
  border-radius: ${convertToVwMob(8)};
  border: ${({ hasImage }) => hasImage ? `3px solid #2BC2E1` : 'none'};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
`;

const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(16)};
`;

const PreviewVideoIcon = styled.svg`
  width: ${convertToVwMob(70)};
  height: ${convertToVwMob(70)};
`;

const PreviewNoneIcon = styled.svg`
  width: ${convertToVwMob(41)};
  height: ${convertToVwMob(41)};
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(70)};
  height: ${convertToVwMob(70)};
`;

const PlaceholderText = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[50]};
  min-height: ${convertToVwMob(30)};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: pre-line;
  line-height: 150%;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${convertToVwMob(8)};
  padding: ${convertToVwMob(16)} ${convertToVwMob(32)} ${convertToVwMob(32)};
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  flex-shrink: 0;
`;

const FooterSpacer = styled.div`
  flex: 1;
`;

const BackButton = styled.button<{ disabled?: boolean }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  background: #FFFFFF;
  border: 1px solid #DFDFDF;
  border-radius: ${convertToVwMob(4)};
  box-sizing: border-box;
  padding: 0;
  ${({ theme }) => theme.typography.button.regular20}
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  color: #676767;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const NextButton = styled.button<{ disabled?: boolean; $loading?: boolean }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  background: ${({ disabled, $loading }) => ($loading ? '#EBFCFF' : disabled ? '#DFDFDF' : '#026478')};
  border: none;
  border-radius: ${convertToVwMob(4)};
  box-sizing: border-box;
  padding: 0;
  ${({ theme }) => theme.typography.button.regular20}
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  color: ${({ disabled, $loading }) => ($loading ? '#2BC2E1' : disabled ? '#BCBCBC' : '#FFFFFF')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  white-space: nowrap;
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
  width: ${convertToVwMob(132)};
  height: ${convertToVwMob(132)};
  flex: 0 0 auto;
`;

// 2단계 전용 스타일
const Step2Container = styled.div`
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(20)};
`;

const EditorTapArea = styled.div<{ $focused?: boolean }>`
  width: 100%;
  border-radius: ${({ $focused }) => ($focused ? 0 : convertToVwMob(4))};
  border: ${({ $focused }) => ($focused ? 'none' : '1px solid #eee')};
  background: ${({ $focused }) => ($focused ? 'transparent' : '#fff')};
  overflow: ${({ $focused }) => ($focused ? 'visible' : 'hidden')};

  > div {
    margin-top: 0;
  }

  > div > div {
    margin-top: 0;
    border-radius: 0;
  }
`;

const Step2PreviewArea = styled.div`
  width: 100%;
  height: ${convertToVwMob(220)};
  background: #F0F0F0;
  border-radius: ${convertToVwMob(8)};
  border: 3px solid #2BC2E1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;


const MainImageBadge = styled.div`
  position: absolute;
  top: ${convertToVwMob(8)};
  left: ${convertToVwMob(8)};
  height: ${convertToVwMob(28)};
  padding: 0 ${convertToVwMob(12)};
  background: ${({ theme }) => theme.colors.primaryBlue[40]};
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: ${convertToVwMob(4)};
  ${({ theme }) => theme.typography.caption.bold14}
  font-size: ${convertToVwMob(14)};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(4)};
`;

const RemoveMediaButton = styled.button`
  position: absolute;
  top: ${convertToVwMob(8)};
  right: ${convertToVwMob(8)};
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  background: rgba(0, 0, 0, 0.4);
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  font-size: ${convertToVwMob(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  border-radius: 50%;
  
  &:active {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const AddPhotoButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  width: 100%;
  height: ${convertToVwMob(46)};
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: ${convertToVwMob(12)};
  background: #ffffff;
  box-sizing: border-box;
  cursor: pointer;
  color: #676767;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const AddPhotoLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 130%;
  color: #676767;
`;

const AddPhotoIcon = styled.img`
  width: ${convertToVwMob(28)};
  height: ${convertToVwMob(28)};
  margin-right: ${convertToVwMob(8)};
`;

const KeyboardFixedFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1002;
  display: flex;
  flex-direction: column;
  padding: ${convertToVwMob(8)};
  padding-bottom: calc(${convertToVwMob(8)} + env(safe-area-inset-bottom));
  background: #fdfdfd;
  border-top: 1px solid #bcbcbc;
  flex-shrink: 0;
`;

// 3단계 전용 스타일
const Step3Container = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(20)} ${convertToVwMob(24)};
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Step3Desc = styled.p`
  margin: 0 0 ${convertToVwMob(16)};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const PreviewContent = styled.div<{ $hasMedia?: boolean }>`
  margin-top: ${({ $hasMedia }) => ($hasMedia ? convertToVwMob(16) : 0)};
  width: 100%;
  min-height: ${convertToVwMob(300)};
  ${({ theme }) => theme.typography.contents.regular20}
  font-size: ${convertToVwMob(20)};
  color: #17171B;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 150%;
`;

export default CreatePostModalMob;

